'use client';

import { Plus, Snowflake } from 'lucide-react';
import { cn } from '~/lib/utils';
import type { CardsResponse } from '~/routes/_authed/card/-hooks/use-card';

type CardAccount = CardsResponse['accounts'][number];

interface CardListProps {
  cards: CardAccount[];
  activeCardId: string | null;
  onSelectCard: (id: string) => void;
  onAddCard: () => void;
}

function CardMiniVisual({
  card,
  isActive,
  onClick,
}: {
  card: CardAccount;
  isActive: boolean;
  onClick: () => void;
}) {
  const label =
    (card.metadata?.card_name as string) ||
    (card.metadata?.name as string) ||
    'Tarjeta';
  const isFrozen = card.status === 'frozen';

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative flex h-[7.4rem] w-[11.5rem] shrink-0 snap-center flex-col justify-between rounded-2xl border p-3.5 transition-all duration-200',
        isActive
          ? 'border-foreground bg-foreground text-background shadow-[0_20px_28px_-22px_color-mix(in_oklch,var(--foreground)_80%,transparent)]'
          : 'border-border bg-card text-foreground shadow-[0_14px_25px_-26px_color-mix(in_oklch,var(--foreground)_55%,transparent)]',
      )}
      aria-label={`Tarjeta ${label}, terminada en ${card.lastFour}`}
      aria-pressed={isActive}
    >
      <div className="flex items-center justify-between">
        <span
          className={cn(
            'text-[10px] font-semibold tracking-wider uppercase',
            isActive ? 'text-background/80' : 'text-muted-foreground',
          )}
        >
          {label}
        </span>
        {isFrozen && (
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px]',
              isActive
                ? 'bg-background/15 text-background/80'
                : 'bg-muted text-muted-foreground',
            )}
          >
            <Snowflake className="h-2.5 w-2.5" strokeWidth={1.5} />
            Frozen
          </span>
        )}
      </div>

      <div className="flex flex-col gap-0.5">
        <p
          className={cn(
            'font-mono text-xs tracking-widest',
            isActive ? 'text-background' : 'text-foreground',
          )}
        >
          {'•••• '}
          {card.lastFour}
        </p>
        <p
          className={cn(
            'text-[9px]',
            isActive ? 'text-background/50' : 'text-muted-foreground',
          )}
        >
          VIRTUAL
        </p>
      </div>
    </button>
  );
}

export function CardList({
  cards,
  activeCardId,
  onSelectCard,
  onAddCard,
}: CardListProps) {
  return (
    <div
      className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-none"
      role="listbox"
      aria-label="Mis tarjetas"
    >
      {cards.map((card) => (
        <CardMiniVisual
          key={card.id}
          card={card}
          isActive={card.id === activeCardId}
          onClick={() => onSelectCard(card.id)}
        />
      ))}
      <button
        type="button"
        onClick={onAddCard}
        className="flex h-[7.4rem] w-[11.5rem] shrink-0 snap-center flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border bg-card/35 transition-colors hover:bg-muted"
        aria-label="Crear nueva tarjeta"
      >
        <div className="flex h-9 w-9 items-center justify-center rounded-2xl border border-border">
          <Plus className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        </div>
        <span className="text-[10px] font-medium text-muted-foreground">
          + Nueva tarjeta
        </span>
      </button>
    </div>
  );
}
